import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mt-12" data-testid={`policy-section-${n}`}>
    <h2 className="font-display text-2xl md:text-3xl text-[#F5F2F0] leading-tight">
      <span className="text-[#FF6B6B] mr-3">{n}.</span>
      {title}
    </h2>
    <div className="mt-4 space-y-4 text-[#C9C4C1] text-base leading-relaxed">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0F0E0E] text-[#F5F2F0]">
      {/* Top bar */}
      <header className="border-b border-[#2A2626]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="policy-logo">
            <span className="font-display text-2xl tracking-tight text-[#F5F2F0]">Przemiana</span>
            <span className="text-[#FF6B6B] font-display text-2xl">1:1</span>
          </Link>
          <Link
            to="/"
            data-testid="policy-back"
            className="btn-ghost rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Powrót
          </Link>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF6B6B]">Dokument</p>
        <h1 className="font-display font-light text-4xl md:text-6xl mt-4 leading-[0.95]">
          Polityka prywatności
        </h1>

        <Section n={1} title="Administrator danych osobowych">
          <p>
            Administratorem danych osobowych przetwarzanych za pośrednictwem niniejszej strony
            internetowej jest:
          </p>
          <p className="text-[#F5F2F0]">
            Fizjoteryk Eryk Rezler
            <br />
            ul. Młynarska 39/137
            <br />
            05-500 Piaseczno
            <br />
            NIP: 1231536126
          </p>
          <p>
            Kontakt w sprawach dotyczących danych osobowych:{" "}
            <a href="mailto:kontakt@projektzdrowie.eu" className="text-[#FF6B6B] hover:underline">
              kontakt@projektzdrowie.eu
            </a>
          </p>
        </Section>

        <Section n={2} title="Jakie dane przetwarzamy?">
          <p>W zależności od sposobu korzystania ze strony możemy przetwarzać:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>imię,</li>
            <li>numer telefonu,</li>
            <li>wskazany cel dotyczący zmiany masy ciała,</li>
            <li>informacje związane z udzieleniem zgody,</li>
            <li>datę przesłania formularza,</li>
            <li>
              zdjęcie przesłane dobrowolnie do narzędzia „Przemiana AI" – wyłącznie w celu
              wygenerowania wizualizacji,
            </li>
            <li>dane techniczne niezbędne do prawidłowego działania i bezpieczeństwa strony.</li>
          </ul>
        </Section>

        <Section n={3} title="Formularz kontaktowy">
          <p>Dane przekazane poprzez formularz kontaktowy są przetwarzane w celu:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>udzielenia odpowiedzi na zapytanie,</li>
            <li>skontaktowania się w sprawie konsultacji lub treningów personalnych,</li>
            <li>przedstawienia informacji dotyczących usług, o które pyta użytkownik,</li>
            <li>podjęcia działań na żądanie użytkownika przed ewentualnym zawarciem umowy.</li>
          </ul>
          <p>
            Podanie danych jest dobrowolne, jednak bez podania danych wymaganych w formularzu kontakt
            może nie być możliwy.
          </p>
          <p>
            Dane przesłane przez formularz są zapisywane w bazie danych wykorzystywanej przez stronę
            oraz przesyłane na adres e-mail Administratora.
          </p>
        </Section>

        <Section n={4} title="Narzędzie „Przemiana AI”">
          <p>
            Strona umożliwia dobrowolne przesłanie zdjęcia w celu stworzenia poglądowej wizualizacji
            możliwej zmiany sylwetki przy wykorzystaniu technologii sztucznej inteligencji.
          </p>
          <p>Przesłane zdjęcie:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>jest wykorzystywane wyłącznie w celu wykonania żądanej przez użytkownika wizualizacji,</li>
            <li>
              nie jest przez Administratora trwale zapisywane w bazie danych ani pamięci masowej
              strony,
            </li>
            <li>nie jest dołączane do formularza kontaktowego,</li>
            <li>nie jest przesyłane Administratorowi pocztą elektroniczną,</li>
            <li>jest przetwarzane w pamięci systemu przez czas niezbędny do wykonania operacji.</li>
          </ul>
          <p>
            W celu wygenerowania wizualizacji zdjęcie jest przekazywane za pośrednictwem
            infrastruktury technologicznej wykorzystywanej przez stronę do zewnętrznego dostawcy
            technologii sztucznej inteligencji – Google Gemini.
          </p>
          <p>
            Zewnętrzni dostawcy technologiczni mogą przetwarzać dane zgodnie z własnymi zasadami
            dotyczącymi bezpieczeństwa, retencji danych oraz świadczenia usług.
          </p>
          <p>
            Administrator nie wykorzystuje przesłanych zdjęć do tworzenia własnej bazy zdjęć
            użytkowników.
          </p>
          <p>
            Wygenerowana przez AI wizualizacja również nie jest przez Administratora trwale
            zapisywana i jest prezentowana użytkownikowi w ramach bieżącej sesji korzystania ze
            strony.
          </p>
          <p>
            Wizualizacja wygenerowana przez AI ma wyłącznie charakter poglądowy i motywacyjny. Nie
            stanowi przewidywania ani gwarancji rzeczywistego rezultatu treningowego, zdrowotnego lub
            zmiany wyglądu.
          </p>
        </Section>

        <Section n={5} title="Odbiorcy danych">
          <p>
            W związku z funkcjonowaniem strony dane mogą być powierzane podmiotom zapewniającym
            rozwiązania techniczne niezbędne do jej działania, w szczególności dostawcom:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>hostingu i infrastruktury aplikacji,</li>
            <li>baz danych,</li>
            <li>usług poczty elektronicznej,</li>
            <li>technologii sztucznej inteligencji,</li>
            <li>usług technicznych i informatycznych.</li>
          </ul>
          <p>
            Obecnie strona wykorzystuje między innymi rozwiązania Emergent, Google Gemini oraz Resend.
          </p>
          <p>Dane są udostępniane wyłącznie w zakresie niezbędnym do świadczenia określonej usługi.</p>
          <p>
            Niektórzy dostawcy usług mogą przetwarzać dane poza Europejskim Obszarem Gospodarczym. W
            takich przypadkach zastosowanie znajdują odpowiednie mechanizmy prawne wymagane przez
            przepisy dotyczące ochrony danych osobowych.
          </p>
        </Section>

        <Section n={6} title="Jak długo przechowujemy dane?">
          <p>
            Dane przekazane poprzez formularz kontaktowy mogą być przechowywane przez okres niezbędny
            do obsługi zapytania, prowadzenia kontaktu dotyczącego usług oraz przez okres wymagany do
            zabezpieczenia ewentualnych roszczeń lub wykonania obowiązków prawnych Administratora.
          </p>
          <p>
            Zdjęcia przesyłane do narzędzia „Przemiana AI" oraz wygenerowane wizualizacje nie są przez
            Administratora trwale zapisywane. Po stronie strony internetowej są przetwarzane wyłącznie
            w zakresie koniecznym do wykonania bieżącego żądania i prezentacji wyniku użytkownikowi.
          </p>
          <p>
            Zewnętrzni dostawcy technologiczni mogą stosować własne okresy technicznej retencji
            zgodnie z warunkami świadczonych przez nich usług.
          </p>
        </Section>

        <Section n={7} title="Prawa użytkownika">
          <p>
            Osobie, której dane dotyczą, przysługują – w przypadkach określonych przepisami – prawa
            do:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>dostępu do swoich danych,</li>
            <li>sprostowania danych,</li>
            <li>usunięcia danych,</li>
            <li>ograniczenia przetwarzania,</li>
            <li>przenoszenia danych,</li>
            <li>wniesienia sprzeciwu wobec przetwarzania,</li>
            <li>cofnięcia zgody, jeżeli podstawą określonego przetwarzania jest zgoda.</li>
          </ul>
          <p>
            Cofnięcie zgody nie wpływa na zgodność z prawem przetwarzania dokonanego przed jej
            cofnięciem.
          </p>
          <p>
            W sprawach dotyczących realizacji swoich praw można skontaktować się z Administratorem pod
            adresem:{" "}
            <a href="mailto:kontakt@projektzdrowie.eu" className="text-[#FF6B6B] hover:underline">
              kontakt@projektzdrowie.eu
            </a>
          </p>
          <p>
            Użytkownik ma również prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych.
          </p>
        </Section>

        <Section n={8} title="Bezpieczeństwo danych">
          <p>
            Administrator stosuje odpowiednie rozwiązania organizacyjne i techniczne mające na celu
            ochronę danych przed nieuprawnionym dostępem, utratą, zmianą lub ujawnieniem.
          </p>
          <p>
            Zakres przetwarzanych danych jest ograniczany do informacji niezbędnych do realizacji
            określonych funkcji strony.
          </p>
        </Section>

        <Section n={9} title="Pliki cookies i technologie podobne">
          <p>
            Strona może korzystać z plików cookies lub podobnych technologii niezbędnych do jej
            prawidłowego i bezpiecznego działania.
          </p>
          <p>
            Jeżeli w przyszłości na stronie zostaną uruchomione dodatkowe narzędzia analityczne lub
            marketingowe wymagające zgody użytkownika, sposób informowania o tych technologiach oraz
            mechanizm zarządzania zgodami zostaną odpowiednio dostosowane.
          </p>
        </Section>

        <Section n={10} title="Zmiany Polityki prywatności">
          <p>
            Polityka prywatności może być aktualizowana w przypadku zmian funkcjonalności strony,
            wykorzystywanych usług technologicznych lub obowiązujących przepisów.
          </p>
          <p>Aktualna wersja Polityki prywatności jest zawsze dostępna na stronie internetowej.</p>
        </Section>

        <div className="mt-16 pt-8 border-t border-[#2A2626]">
          <Link
            to="/"
            className="btn-primary rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] inline-flex items-center gap-2"
            data-testid="policy-home-button"
          >
            <ArrowLeft className="w-4 h-4" /> Wróć na stronę główną
          </Link>
        </div>
      </main>
    </div>
  );
}
